<template>
  <layout class="captain-payment-details-container">
    <div class="w-full flex adm-container sm:flex-col lg:flex-row">
      <div class="search-input sm:w-full lg:w-1/2 sm:mb-4 lg:mb-0">
        <div class="w-full h-full flex items-center justify-center">
          <span class="w-full">
            <i class="fa fa-angle-left mr-2" />
            <router-link
              :to="`/captain-management/payment-statements/invoice/start/${$route.params.startDate}/end/${$route.params.endDate}`"
              class="font-bold no-underline text-black">{{ driver.name || '-' }}</router-link>
          </span>
        </div>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4 sm:mb-4 lg:mb-0" />
      <div class="flex items-center justify-end text-primary font-bold sm:mb-8 lg:mb-0 sm:w-full lg:w-1/4">
        <download-csv
          :download-url="csvUrl"
          :file-name="`${driver.name} - Trips List`"
        />
      </div>
    </div>
    <div class="w-full flex flex-col adm-container">
      <div class="table-loader-container relative">
        <pt-list>
          <div class="font-bold bg-transparent sm:hidden lg:flex sm:p-0 lg:p-4 sm:border-b-0 lg:border-b">
            <div class="flex-1">Trip ID</div>
            <div class="flex-1 text-right">Regular Fare</div>
            <div class="flex-1 text-right">Pathao Fee</div>
            <div class="flex-1 text-right">Boost</div>
            <div class="flex-1 text-right">Surge</div>
            <div class="flex-1 text-right">Fare Adjustment</div>
            <div class="flex-1 text-right">Cash Collected</div>
            <div class="flex-1 text-right">Digital Payment</div>
            <div class="flex-1 text-right">Trip Distance</div>
            <div class="flex-1 text-right">Trip Time</div>
          </div>
          <pt-list-item
            v-for="(tripRow, tripRowIndex) in tripList"
            :key="tripRowIndex"
          >
            <div class="flex flex-1 sm:flex-col lg:flex-row">
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Trip ID: </span>
                <router-link
                  :to="`/captain-management/payment-statements/start/${$route.params.startDate}/end/${$route.params.endDate}/trip-details/${tripRow.trip_id}`"
                  class="flex no-underline cursor-pointer text-black hover:text-grey-darker items-center font-bold"
                >
                  {{ tripRow.hashed_trip_id }}
                </router-link>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Regular Fare:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.regular_fare) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Pathao Fee:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.pathao_fee) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Boost:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.boost) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Surge:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.surge) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Fare Adjustment:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.fare_adjustment) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Cash Collected:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.cash_collected) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Digital Payment:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ CurrencyConverter(tripRow.wallet_payment) }}</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Trp Distance:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ tripRow.total_distance }} km</span>
              </div>
              <div class="flex flex-1 sm:mb-1 lg:mb-0">
                <span class="font-bold lg:hidden sm:mr-2 lg:mr-0">Trip Time:</span>
                <span class="flex-1 sm:text-left lg:text-right">{{ tripRow.total_time }} min</span>
              </div>
            </div>
          </pt-list-item>
        </pt-list>

        <table-placeholder
          :is-syncing="isLoadingDriverInfo || isLoadingDriverList"
          :data-length="tripList.length"
          loader-class="text-black"
        />
      </div>
      <pt-pagination
        :total-rows="totalRows"
        :current-page="currentPage"
        :page-size="pageSize"
        class="mt-8"
        @page-changed="getTripList"
      />
    </div>
  </layout>
</template>

<script>
import { PtDropdown, PtDropdownItem } from '@pathao/ui-components/esm/Dropdown';
import { PtRadioGroup, PtRadio } from '@pathao/ui-components/esm/FormComponents';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtLoader } from '@pathao/ui-components/esm/Loader';
import { PtList, PtListItem } from '@pathao/ui-components/esm/List';
import Layout from '@layouts/main';
import TablePlaceholder from '../../../../components/custom/TablePlaceholder';
import DownloadCsv from '../../../../components/custom/DownloadCsv';
import makeRequest from '../../../../utils/apiRequest';
import Config from '../../../../config';
import SuggestionInput from '../../../../components/custom/SuggestionInput';
import SelectedDates from '../../../../utils/selected-dates';
import CurrencyConverter from '../../../../plugins/CurrencyConverter';

const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
const TRIPS_LIST_BASE_URL = `${Config.api.baseURL}api/partners/ride-payment-statements/list`;

export default {
  name: 'CaptainPaymentDetails',
  page: {
    title: 'Driver Payment Details',
  },
  components: { SuggestionInput, PtDropdown, PtDropdownItem, PtPagination, Layout, PtRadioGroup, PtRadio, PtLoader, TablePlaceholder, DownloadCsv, PtList, PtListItem },
  mixins: [ CurrencyConverter ],
  data() {
    return {
      tripList: [],
      driver: {},
      date: {
        from: this.$route.params.startDate,
        to: this.$route.params.endDate,
      },
      totalRows: 0,
      currentPage: DEFAULT_CURRENT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      isLoadingDriverInfo: false,
      isLoadingDriverList: false,
    };
  },
  computed: {
    csvUrl() {
      const baseUrl = `${TRIPS_LIST_BASE_URL}/export`;
      const selectedDates = SelectedDates(this.date);
      const paramList = [];

      if (this.$route.params.driverId) {
        paramList.push(`filterByDriverId=${this.$route.params.driverId}`);
      }

      paramList.push(`filterByStartDate=${selectedDates.filterByStartDate}`);
      paramList.push(`filterByEndDate=${selectedDates.filterByEndDate}`);

      return paramList.length ? `${baseUrl}?${paramList.join('&')}` : baseUrl;
    },
  },
  mounted() {
    this.getDriverInfo();
    this.getTripList();
  },
  methods: {
    getDriverInfo() {
      if (!this.$route.params.driverId) {
        this.$notification.error('Error in getting driver information');
        return;
      }

      const driverMeta = {
        url: `${Config.api.baseURL}api/partners/drivers/${this.$route.params.driverId}`,
      };

      this.isLoadingDriverInfo = true;

      makeRequest(driverMeta)
        .then((response) => {
          this.driver = response.data.data;
          this.isLoadingDriverInfo = false;
        })
        .catch((errorResponse) => {
          this.isLoadingDriverInfo = false;
          this.$notification.error('Error in getting driver information');
          console.error(errorResponse);
        });
    },
    getTripList(currentPage = DEFAULT_CURRENT_PAGE, pageSize = DEFAULT_PAGE_SIZE) {
      const selectedDates = SelectedDates(this.date);

      let tripListMeta = {
        url: TRIPS_LIST_BASE_URL,
        params: {
          driverId: this.$route.params.driverId,
          ...selectedDates,
          per_page: pageSize,
          page: currentPage,
        },
      };

      this.isLoadingDriverList = true;

      makeRequest(tripListMeta)
        .then((listResponse) => {
          this.tripList = listResponse.data.data;
          this.totalRows = listResponse.data.pagination.total;
          this.currentPage = listResponse.data.pagination.current_page;
          this.pageSize = listResponse.data.pagination.per_page;

          this.isLoadingDriverList = false;
        })
        .catch((errorResponse) => {
          this.isLoadingDriverList = false;
          this.$notification.error('Error in getting payment list');
          console.error(errorResponse);
        });
    },
    setDate(date) {
      this.date = date;
      this.getTripList();
    },
  },
};
</script>
